import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Share, Plus, ArrowDown } from 'lucide-react';

const DISMISS_KEY = 'mbn_install_dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

export default function InstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const handleBeforeInstall = useCallback((e) => {
    e.preventDefault();
    setDeferredPrompt(e);
  }, []);

  useEffect(() => {
    // Don't show if already installed
    if (isStandalone()) return;

    // Don't show if recently dismissed
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < DISMISS_DURATION) return;

    // Listen for the beforeinstallprompt event (Android/Chrome)
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show banner after a short delay
    const timer = setTimeout(() => setShowBanner(true), 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearTimeout(timer);
    };
  }, [handleBeforeInstall]);

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Chrome: trigger native install prompt
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      // iOS: show step-by-step guide
      setShowIOSGuide(true);
    } else {
      // Fallback for other browsers
      setShowIOSGuide(true);
    }
  };

  if (!showBanner || isStandalone()) return null;

  return (
    <>
      {/* Install Banner */}
      <div
        className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-500"
        data-testid="install-app-banner"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#5b7f95] to-[#4a6b80] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Download className="w-5 h-5" />
              <span className="font-semibold text-sm">Get the App</span>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="dismiss-install-banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-5 py-4">
            <p className="text-sm text-[#2d3748] mb-3">
              Add Mom's Biz Network to your home screen for quick access — just like a real app!
            </p>
            <Button
              onClick={handleInstallClick}
              className="w-full bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full py-5 text-base font-semibold"
              data-testid="install-app-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Install App
            </Button>
          </div>
        </div>
      </div>

      {/* iOS / Fallback Step-by-Step Guide Overlay */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowIOSGuide(false); }}
          data-testid="ios-install-guide"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in slide-in-from-bottom-8 duration-300">
            <div className="bg-gradient-to-r from-[#5b7f95] to-[#4a6b80] px-5 py-4 flex items-center justify-between">
              <h3 className="text-white font-bold text-lg">Add to Home Screen</h3>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="text-white/70 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-5 space-y-5">
              {isIOS() ? (
                <>
                  {/* iOS Instructions */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5b7f95] text-white flex items-center justify-center font-bold text-lg">1</div>
                    <div>
                      <p className="font-semibold text-[#2d3748]">Tap the Share button</p>
                      <p className="text-sm text-[#64748b] mt-1">
                        At the bottom of Safari, tap the
                        <Share className="inline w-4 h-4 mx-1 text-[#5b7f95]" />
                        share icon
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5b7f95] text-white flex items-center justify-center font-bold text-lg">2</div>
                    <div>
                      <p className="font-semibold text-[#2d3748]">Scroll down & tap "Add to Home Screen"</p>
                      <p className="text-sm text-[#64748b] mt-1">
                        Look for the
                        <Plus className="inline w-4 h-4 mx-1 text-[#5b7f95]" />
                        <span className="font-medium text-[#2d3748]">Add to Home Screen</span> option
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#cb9944] text-white flex items-center justify-center font-bold text-lg">3</div>
                    <div>
                      <p className="font-semibold text-[#2d3748]">Tap "Add"</p>
                      <p className="text-sm text-[#64748b] mt-1">
                        The MBN app icon will appear on your home screen!
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Android / Generic Instructions */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5b7f95] text-white flex items-center justify-center font-bold text-lg">1</div>
                    <div>
                      <p className="font-semibold text-[#2d3748]">Open browser menu</p>
                      <p className="text-sm text-[#64748b] mt-1">
                        Tap the <span className="font-mono font-bold text-[#2d3748]">&#8942;</span> three dots in the top-right corner of your browser
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#5b7f95] text-white flex items-center justify-center font-bold text-lg">2</div>
                    <div>
                      <p className="font-semibold text-[#2d3748]">Tap "Add to Home screen"</p>
                      <p className="text-sm text-[#64748b] mt-1">
                        Or "Install app" if you see that option
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#cb9944] text-white flex items-center justify-center font-bold text-lg">3</div>
                    <div>
                      <p className="font-semibold text-[#2d3748]">Tap "Add" to confirm</p>
                      <p className="text-sm text-[#64748b] mt-1">
                        The MBN app icon will appear on your home screen!
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className="bg-[#f0f5f8] rounded-xl p-4 text-center">
                <p className="text-sm text-[#5b7f95] font-medium">
                  Once installed, open MBN from your home screen for the full app experience!
                </p>
              </div>

              <Button
                onClick={() => setShowIOSGuide(false)}
                className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-5"
                data-testid="close-install-guide-btn"
              >
                Got it!
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
